import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { QueryRef } from 'apollo-angular';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { BaseComponent } from '../../base/base.component';
import { Awards } from 'src/app/model/award';
import { StoreCategory, StoreProduct, StoreSortKey } from 'src/app/model/store';
import { CartItem, CartSyncResult } from 'src/app/model/cart';
import { GetAllAwardsGQL, GetAllAwardsQuery } from 'graphql/awards/awards.definition';
import { CreateBuyMovGQL } from 'graphql/buy/buy.definition';
import { CartService } from '../../service/cart.service';
import { WalletService } from '../../service/wallet.service';
import {
  applyCanonicalCategoryLabels,
  buildCategories,
  isAwardActive,
  normalizeText,
  sortProducts,
  toStoreProduct,
} from './store.utils';

@Component({
  selector: 'app-clic-store',
  templateUrl: './clic-store.component.html',
  styleUrls: ['./clic-store.component.scss'],
})
export class ClicStoreComponent extends BaseComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  /** Catalogo completo vindo da API; nao e reordenado no lugar. */
  allProducts: StoreProduct[] = [];
  /** O que o *ngFor da grade le. Recalculado so por evento de filtro. */
  visibleProducts: StoreProduct[] = [];
  categories: StoreCategory[] = [];

  searchTerm = '';
  sort: StoreSortKey = 'RELEVANCE';
  selectedCategoryKey: string = null;

  private searchTerm$ = new Subject<string>();
  allAwardsQuery: QueryRef<GetAllAwardsQuery>;

  // Estado derivado do carrinho, lido pelo template em O(1).
  cartItems: CartItem[] = [];
  cartQuantities: { [awardId: string]: number } = {};
  cartCount = 0;

  isCartOpen = false;
  submitting = false;

  // Estado da tela de sucesso, no lugar do SweetAlert + window.location.reload().
  isSuccessOpen = false;
  successItems: CartItem[] = [];
  successPoints = 0;
  successProtocol = '';

  re: string;

  walletValue = 0;
  walletLoaded = false;
  /** Saldo nao pode ser lido: nao bloqueia o catalogo, avisa na barra. */
  walletUnknown = false;
  valueOfDebit = 0;
  valueAfterDebit = 0;

  deliveryPhone = '';

  /** Evita empilhar dois SweetAlert quando a troca falha por estoque. */
  private suppressNextCartWarning = false;

  constructor(
    public router: Router,
    public translate: TranslateService,
    public route2: ActivatedRoute,
    public getAllAwardsGQL: GetAllAwardsGQL,
    private cartService: CartService,
    private walletService: WalletService,
    private createBuyMovGQL: CreateBuyMovGQL
  ) {
    super(router, translate);
  }

  ngOnInit() {
    // Antes de tudo: descarta carrinho de uma sessao anterior nesta mesma aba.
    this.cartService.reloadFromStorage();
    this.getAwards();
    this.getUrlParams();

    // Sem debounce cada tecla refaz filtro e ordenacao do catalogo inteiro
    // dentro do ciclo de deteccao de mudanca do input.
    this.searchTerm$
      .pipe(debounceTime(200), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((term) => {
        this.searchTerm = term;
        this.recomputeVisible();
      });

    this.cartService.items$.pipe(takeUntil(this.destroy$)).subscribe((items) => {
      this.cartItems = items;
      this.cartQuantities = this.cartService.quantityMap();
      this.cartCount = this.cartService.totalItems;
      this.valueOfDebit = this.cartService.totalPoints;
      this.recomputeBalance();
    });

    this.walletService.walletValue$.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      this.walletValue = value;
      this.recomputeBalance();
    });
    this.walletService.loaded$.pipe(takeUntil(this.destroy$)).subscribe((loaded) => {
      this.walletLoaded = loaded;
    });
    this.walletService.unknown$.pipe(takeUntil(this.destroy$)).subscribe((unknown) => {
      this.walletUnknown = unknown;
    });

    // A Loja carrega o proprio saldo: antes dependia do header ter rodado antes.
    this.walletService.load().pipe(takeUntil(this.destroy$)).subscribe();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getUrlParams() {
    this.route2.params.pipe(takeUntil(this.destroy$)).subscribe((url) => {
      if (!this.isNullOrUndefined(url)) {
        this.re = url.re;
      }
    });
  }

  getAwards() {
    this.allAwardsQuery = this.getAllAwardsGQL.watch();
    this.allAwardsQuery.valueChanges.pipe(takeUntil(this.destroy$)).subscribe({
      next: ({ data }: any) => {
        const list = (data && data.getAllAwards) || [];
        this.allProducts = list
          .filter((item: Awards) => isAwardActive(item.status))
          .map((item: Awards) => toStoreProduct(item));
        this.categories = buildCategories(this.allProducts);
        applyCanonicalCategoryLabels(this.allProducts, this.categories);
        // Categoria escolhida pode ter sumido do catalogo entre recargas.
        if (this.selectedCategoryKey && !this.categories.some((c) => c.key === this.selectedCategoryKey)) {
          this.selectedCategoryKey = null;
        }
        this.recomputeVisible();

        // Catalogo vazio costuma ser falha de carga: reconciliar contra ele
        // apagaria o carrinho inteiro, inclusive no sessionStorage.
        if (!this.allProducts.length) {
          return;
        }
        // Estoque e preco podem ter mudado desde que o item entrou no carrinho.
        const sync = this.cartService.syncWithCatalog(this.allProducts);
        this.warnAboutCartChanges(sync);
      },
      error: (error) => this.onError(error),
    });
  }

  // ----------------------------------------------------------------- filtros

  onSearchTermChange(term: string): void {
    this.searchTerm$.next(term || '');
  }

  onSortChange(sort: StoreSortKey): void {
    this.sort = sort;
    this.recomputeVisible();
  }

  onCategoryChange(key: string): void {
    this.selectedCategoryKey = key;
    this.recomputeVisible();
  }

  clearFilters(): void {
    this.searchTerm = '';
    this.searchTerm$.next('');
    this.selectedCategoryKey = null;
    this.recomputeVisible();
  }

  get hasFilters(): boolean {
    return !!this.searchTerm || !!this.selectedCategoryKey;
  }

  /**
   * Array derivado recalculado por evento, nao getter: um getter devolveria
   * nova referencia a cada ciclo de deteccao de mudanca e reconstruiria o
   * *ngFor dezenas de vezes por segundo.
   */
  private recomputeVisible(): void {
    const byCategory = this.selectedCategoryKey
      ? this.allProducts.filter((p) => p.categoryKey === this.selectedCategoryKey)
      : this.allProducts;

    const key = normalizeText(this.searchTerm);
    const byTerm = key ? byCategory.filter((p) => p.searchKey.indexOf(key) !== -1) : byCategory;

    this.visibleProducts = sortProducts(byTerm, this.sort);
  }

  private readEmployeeId(): string {
    try {
      return localStorage.getItem('employeeId') || '';
    } catch (e) {
      return '';
    }
  }

  /** O carrinho pode encolher sozinho ao recarregar o catalogo; avisar e obrigatorio. */
  private warnAboutCartChanges(sync: CartSyncResult): void {
    if (this.suppressNextCartWarning) {
      this.suppressNextCartWarning = false;
      return;
    }
    if (!sync || (!sync.removed.length && !sync.clamped.length)) {
      return;
    }
    const parts: string[] = [];
    if (sync.removed.length) {
      parts.push(
        this.translate.instant('clicStore.cartItemsRemoved') + ' ' + sync.removed.join(', ') + '.'
      );
    }
    if (sync.clamped.length) {
      parts.push(
        this.translate.instant('clicStore.cartItemsClamped') + ' ' + sync.clamped.join(', ') + '.'
      );
    }
    this.showMessage(this.translate.instant('attention'), parts.join('<br>'), 'warning');
  }

  private recomputeBalance(): void {
    this.valueAfterDebit = this.walletValue - this.valueOfDebit;
  }

  // ---------------------------------------------------------------- carrinho

  /** Sem trackBy a grade destroi e recria todos os cards a cada recalculo. */
  trackByAwardId(_: number, product: StoreProduct): string {
    return product.id;
  }

  quantityOf(product: Awards): number {
    return this.cartQuantities[product.id] || 0;
  }

  increaseQuantity(product: Awards): void {
    this.cartService.increment(product);
  }

  decreaseQuantity(product: Awards): void {
    this.cartService.decrement(product.id);
  }

  get isCartEmpty(): boolean {
    return this.cartService.isEmpty;
  }

  // ---------------------------------------------------------------- checkout

  openCart(): void {
    this.isCartOpen = true;
  }

  closeCart(): void {
    this.isCartOpen = false;
    // Devolve o foco ao gatilho: quem conhece o botao e a pagina, nao o painel.
    setTimeout(() => {
      const trigger = document.querySelector<HTMLElement>('.store-bar__cart');
      if (trigger) {
        trigger.focus();
      }
    }, 0);
  }

  closeSuccess(): void {
    this.isSuccessOpen = false;
    this.successItems = [];
    this.successPoints = 0;
    this.successProtocol = '';
  }

  goToStatement(): void {
    this.closeSuccess();
    this.router.navigate(['/exchange-points']);
  }

  incrementInCart(awardId: string): void {
    const product = this.allProducts.filter((item) => item.id === awardId)[0];
    if (product) {
      this.cartService.increment(product);
    }
  }

  decrementInCart(awardId: string): void {
    this.cartService.decrement(awardId);
  }

  removeFromCart(awardId: string): void {
    this.cartService.remove(awardId);
  }

  onCreateBuyMovMutation() {
    if (this.isCartEmpty || this.submitting) {
      return;
    }

    // A API publicada (branch `develop`) exige `employeeId` no payload: o
    // resolver de la nao resolve o colaborador pelo token. Sem isso a troca
    // falha com erro de campo obrigatorio.
    const employeeId = this.readEmployeeId();
    if (!employeeId) {
      this.showMessage(
        'Atenção',
        'Não foi possível identificar seu cadastro. Recarregue a página e tente novamente.',
        'warning'
      );
      return;
    }

    this.submitting = true;
    // `idempotencyKey` so existe na branch `fix-loja-resgate` da API; enviar
    // para a versao publicada quebra a mutation com erro de campo desconhecido.
    // Ate ela subir, o duplo clique e barrado pelo `submitting`.
    const createBuyMovDto: any = {
      employeeId,
      phoneDelivery: this.deliveryPhone,
      items: this.cartService.toBuyItems(),
    };

    // Sem takeUntil de proposito: se o usuario sair da rota com a requisicao em
    // voo, o servidor conclui a compra e debita - e o carrinho precisa ser
    // limpo mesmo assim, senao da para comprar tudo de novo.
    this.createBuyMovGQL
      .mutate({ createBuyMovDto })
      .subscribe(
        ({ data }: any) => {
          this.submitting = false;
          // Snapshot antes de esvaziar o carrinho: a tela de sucesso lista o
          // que foi trocado.
          this.successItems = this.cartService.items.slice();
          this.successPoints = this.cartService.totalPoints;
          this.successProtocol = (data && data.createBuyMov && data.createBuyMov.id) || '';

          this.cartService.endCheckout();
          this.deliveryPhone = '';
          this.isCartOpen = false;
          this.isSuccessOpen = true;

          // Saldo e estoque vem da API, sem recarregar a pagina inteira.
          this.walletService.refresh().subscribe();
          this.allAwardsQuery.refetch();
        },
        (error) => {
          this.submitting = false;
          this.onError(error);
          // O alerta do erro ("X sem estoque suficiente") e o mais informativo.
          // O refetch abaixo reconcilia o carrinho e dispararia um segundo
          // SweetAlert, que fecharia o primeiro - o Swal so mantem um dialogo.
          this.suppressNextCartWarning = true;
          this.allAwardsQuery.refetch();
        }
      );
  }
}
